/**
 * Per-column min/max metric filters for Ads Manager.
 */

export interface MetricFilterBounds {
  min?: number
  max?: number
}

/** columnId → bounds (omit empty filters). */
export type MetricFiltersState = Record<string, MetricFilterBounds>

const CURRENCY_IDS = new Set([
  "cost",
  "revenue",
  "profit",
  "cpc",
  "cpm",
  "cpa",
  "rpc",
  "rpm",
])

const PERCENT_IDS = new Set([
  "roi",
  "ctr",
  "cvr",
  "p1_cvr",
  "p2_cvr",
  "one_to_two_flow",
])

/** Settings columns — not filterable (ABO / bid live on adgroups). */
const NON_FILTERABLE_METRIC_IDS = new Set(["budget", "bid", "status"])

export function isFilterableMetricColumn(
  columnId: string,
  columnType: "string" | "number",
): boolean {
  return columnType === "number" && !NON_FILTERABLE_METRIC_IDS.has(columnId)
}

export function hasMetricFilterBounds(
  bounds: MetricFilterBounds | undefined,
): boolean {
  if (!bounds) return false
  return bounds.min != null || bounds.max != null
}

export function metricFiltersToApiPayload(
  filters: MetricFiltersState,
): Array<{ id: string; min?: number; max?: number }> {
  return Object.entries(filters)
    .filter(
      ([id, b]) =>
        hasMetricFilterBounds(b) && !NON_FILTERABLE_METRIC_IDS.has(id),
    )
    .map(([id, b]) => {
      const entry: { id: string; min?: number; max?: number } = { id }
      if (b.min != null) entry.min = b.min
      if (b.max != null) entry.max = b.max
      return entry
    })
}

export function serializeMetricFilters(
  filters: MetricFiltersState,
): string | undefined {
  const payload = metricFiltersToApiPayload(filters)
  if (payload.length === 0) return undefined
  return JSON.stringify(payload)
}

function formatBoundValue(columnId: string, value: number): string {
  if (CURRENCY_IDS.has(columnId)) {
    return (
      "$" +
      value.toLocaleString("en-US", {
        minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        maximumFractionDigits: 2,
      })
    )
  }
  if (PERCENT_IDS.has(columnId)) {
    return `${value}%`
  }
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 })
}

export function formatMetricFilterChip(
  columnId: string,
  label: string,
  bounds: MetricFilterBounds,
): string {
  const hasMin = bounds.min != null
  const hasMax = bounds.max != null
  if (hasMin && hasMax) {
    return `${label} ${formatBoundValue(columnId, bounds.min!)}–${formatBoundValue(columnId, bounds.max!)}`
  }
  if (hasMin) {
    return `${label} ≥ ${formatBoundValue(columnId, bounds.min!)}`
  }
  if (hasMax) {
    return `${label} ≤ ${formatBoundValue(columnId, bounds.max!)}`
  }
  return label
}

export function countMetricFilters(filters: MetricFiltersState): number {
  return Object.entries(filters).filter(
    ([id, b]) =>
      hasMetricFilterBounds(b) && !NON_FILTERABLE_METRIC_IDS.has(id),
  ).length
}
